/**
 * Messages Slice — Message CRUD, streaming, sending, retry
 *
 * Handles: sendTextMessage, sendTextMessageStream, stopRecording, stopRecordingStream,
 *          retryConversation, clearMessages
 */

import type { StateCreator } from "zustand";
import * as Sentry from "@sentry/react-native";
import { startNewTrace } from "@sentry/core";
import { uuidv7 } from "uuidv7";
import { getSocket } from "../../services/socket";
import { useAuthStore } from "../authStore";
import { thrownErrorToErrorInfo } from "../../utils/conversationErrors";
import { useStreamingTTSStore } from "../streamingTTSStore";
import { getAudioMode } from "../../hooks/useAudioMode";
import i18n from "../../i18n";
import type { ConversationState } from "../conversationTypes";
import type { AnalyzedMessage, PronunciationResult } from "../../types/conversation";
import {
  log,
  clearErrorMessages,
  streamMessage,
} from "../conversationHelpers";

export interface MessagesSlice {
  messages: AnalyzedMessage[];
  isSendingMessage: boolean;
  isStreaming: boolean;
  streamingText: string;
  streamingMessageId: string | null;
  peerTyping: boolean;

  sendTextMessage: (text: string) => Promise<void>;
  sendTextMessageStream: (text: string) => Promise<void>;
  stopRecording: (
    transcription: string,
    pronunciationResult: PronunciationResult | undefined
  ) => Promise<void>;
  stopRecordingStream: (
    transcription: string,
    pronunciationResult: PronunciationResult | undefined,
    sessionId: string,
    userMessageId: string,
  ) => Promise<void>;
  retryConversation: (messageId: string) => Promise<void>;
  clearMessages: () => void;
}

/**
 * Lazy reference to the store — set by the composer after creation.
 * Needed for streamMessage's onSessionTransition → loadSession callback.
 */
let storeRef: { getState: () => ConversationState } | null = null;

export function setMessagesSliceStoreRef(ref: { getState: () => ConversationState }): void {
  storeRef = ref;
}

export const createMessagesSlice: StateCreator<
  ConversationState,
  [],
  [],
  MessagesSlice
> = (set, get) => ({
  messages: [],
  isSendingMessage: false,
  isStreaming: false,
  streamingText: '',
  streamingMessageId: null,
  peerTyping: false,

  /**
   * Send a text message via Socket.IO streaming.
   * Socket.IO's sendBuffer handles offline queuing automatically.
   */
  sendTextMessage: async (text: string) => {
    const state = get();
    let sessionId = state.currentSessionId;

    // Auto-create session if needed
    if (!sessionId) {
      await get().startNewSession();
      sessionId = get().currentSessionId;
      if (!sessionId) return; // Creation failed, error already set by startNewSession
    }

    await get().sendTextMessageStream(text);
  },

  /**
   * Send a text message via Socket.IO streaming.
   * Creates optimistic messages, pipes tokens into the AI placeholder,
   * and handles analysis asynchronously.
   */
  sendTextMessageStream: async (text: string) => {
    // Cancel any active streaming TTS before sending new message
    useStreamingTTSStore.getState().cancel();

    await startNewTrace(async () => {
      await Sentry.startSpan(
        {
          name: "send-message-stream",
          op: "user.action",
        },
        async (span) => {
          const socket = getSocket();
          if (!socket) {
            // Socket unavailable — shouldn't happen after login
            log.error("Socket not available");
            set({
              error: i18n.t('errors.api.INTERNAL_ERROR'),
              errorInfo: { type: "network", message: "Socket not available", retryable: true },
            });
            return;
          }

          const isQueued = !socket.connected;

          try {
            const { user } = useAuthStore.getState();
            if (!user) {
              throw new Error("User not authenticated");
            }

            if (!text || text.trim().length === 0) {
              throw new Error("Message cannot be empty");
            }

            const state = get();

            const sessionId = state.currentSessionId;
            if (!sessionId) return; // Session must be created from the hub

            // Generate stable user message ID for upsert semantics
            const userMessageId = uuidv7();

            // Create optimistic user message
            const optimisticUser: AnalyzedMessage = {
              id: userMessageId,
              type: "text",
              text: text.trim(),
              userId: user.id,
              sessionId,
              createdAt: new Date(),
              status: isQueued ? "queued" : "sending",
              role: "user",
              analysisPending: get().hasAnalysis,
            };

            // Create placeholder AI message for streaming
            const optimisticAiId = `ai-${Date.now()}-${Math.random()
              .toString(36)
              .substr(2, 9)}`;
            const optimisticAi: AnalyzedMessage = {
              id: optimisticAiId,
              type: "text",
              text: "",
              userId: user.id,
              sessionId,
              createdAt: new Date(),
              status: "streaming",
              role: "assistant",
            };

            set((s) => ({
              messages: [...clearErrorMessages(s.messages), optimisticUser, optimisticAi],
              isSendingMessage: true,
              isStreaming: true,
              streamingText: '',
              streamingMessageId: optimisticAiId,
              error: null,
              errorInfo: null,
            }));

            span.setAttribute("message.length", text.trim().length);
            span.setAttribute("transport", "socket.io");

            // Start streaming TTS if audio mode is auto_play
            if (getAudioMode() === 'auto_play') {
              useStreamingTTSStore.getState().startStreaming(optimisticAiId, get().currentSessionId ?? undefined);
            }

            // Stream message and handle analysis via shared helper
            await streamMessage({
              mode: 'send',
              socket,
              sessionId,
              aiId: optimisticAiId,
              text: text.trim(),
              userMessageId,
              hasAnalysis: get().hasAnalysis,
              span,
              set,
              getConversationStore: () => storeRef!,
            });
          } catch (error: unknown) {
            const errorInfo = thrownErrorToErrorInfo(error);
            span.setStatus({ code: 2, message: errorInfo.message });

            set((s) => ({
              messages: s.messages.map((m) => {
                if (m.role === "assistant" && m.status === "streaming") {
                  return { ...m, status: "error", errorInfo };
                }
                if (m.status === "sending") {
                  return { ...m, status: "complete", analysisPending: false };
                }
                return m;
              }),
              isSendingMessage: false,
              isStreaming: false,
              error: errorInfo.message,
              errorInfo,
            }));
          }
        }
      );
    });
  },

  /**
   * Stop voice recording and send message with pronunciation data.
   * Routes directly to Socket.IO streaming (sendBuffer handles offline queuing).
   */
  stopRecording: async (
    transcription: string,
    pronunciationResult: PronunciationResult | undefined
  ) => {
    // Guard: don't send empty transcriptions
    if (!transcription || transcription.trim().length === 0) {
      log.warn("stopRecording called with empty transcription, ignoring");
      set({ isRecording: false });
      return;
    }

    const { user } = useAuthStore.getState();
    if (!user) {
      set({ isRecording: false });
      log.warn("Cannot stop recording — user not authenticated");
      return;
    }

    const state = get();

    const sessionId = state.currentSessionId;
    if (!sessionId) {
      set({ isRecording: false });
      return; // Session must be created from the hub
    }

    // Client-generated UUID for upsert semantics
    const userMessageId = uuidv7();

    // Create optimistic message. Without real pronunciation scores it becomes
    // a plain text message — same mapping the server-load path applies to
    // scoreless user messages (conversationHelpers.serverMessageToMessage).
    const optimisticMessage: AnalyzedMessage = pronunciationResult
      ? {
          id: userMessageId,
          type: "voice",
          transcription: transcription.trim(),
          pronunciationResult,
          userId: user.id,
          sessionId,
          createdAt: new Date(),
          status: "sending",
          role: "user",
          analysisPending: get().hasAnalysis,
        }
      : {
          id: userMessageId,
          type: "text",
          text: transcription.trim(),
          userId: user.id,
          sessionId,
          createdAt: new Date(),
          status: "sending",
          role: "user",
          analysisPending: get().hasAnalysis,
        };

    set((s) => ({
      messages: [...clearErrorMessages(s.messages), optimisticMessage],
      isRecording: false,
      isSendingMessage: true,
      error: null,
      errorInfo: null,
    }));

    await get().stopRecordingStream(transcription, pronunciationResult, sessionId, userMessageId);
  },

  /**
   * Send a voice message via Socket.IO streaming.
   * Creates an AI placeholder and streams tokens via the shared helper.
   * Called after stopRecording creates the optimistic voice message.
   */
  stopRecordingStream: async (
    transcription: string,
    pronunciationResult: PronunciationResult | undefined,
    sessionId: string,
    userMessageId: string,
  ) => {
    await startNewTrace(async () => {
      await Sentry.startSpan(
        {
          name: "send-voice-message-stream",
          op: "user.action",
        },
        async (span) => {
          const socket = getSocket();
          if (!socket) {
            // Socket unavailable — shouldn't happen after login
            log.error("Socket not available for voice stream");
            set({
              error: i18n.t('errors.api.INTERNAL_ERROR'),
              errorInfo: { type: "network", message: "Socket not available", retryable: true },
              isSendingMessage: false,
            });
            return;
          }

          const isQueued = !socket.connected;

          try {
            const { user } = useAuthStore.getState();
            if (!user) {
              throw new Error("User not authenticated");
            }

            // Update user message status if queued
            if (isQueued) {
              set((s) => ({
                messages: s.messages.map((m) =>
                  m.id === userMessageId
                    ? { ...m, status: "queued" }
                    : m
                ),
              }));
            }

            // Create placeholder AI message for streaming
            const optimisticAiId = `ai-${Date.now()}-${Math.random()
              .toString(36)
              .substr(2, 9)}`;
            const optimisticAi: AnalyzedMessage = {
              id: optimisticAiId,
              type: "text",
              text: "",
              userId: user.id,
              sessionId,
              createdAt: new Date(),
              status: "streaming",
              role: "assistant",
            };

            set((s) => ({
              messages: [...s.messages, optimisticAi],
              isStreaming: true,
              streamingText: '',
              streamingMessageId: optimisticAiId,
            }));

            span.setAttribute("message.length", transcription.trim().length);
            span.setAttribute("transport", "socket.io");

            // Start streaming TTS if audio mode is auto_play
            if (getAudioMode() === 'auto_play') {
              useStreamingTTSStore.getState().startStreaming(optimisticAiId, get().currentSessionId ?? undefined);
            }

            // Stream message and handle analysis via shared helper
            await streamMessage({
              mode: 'send',
              socket,
              sessionId,
              aiId: optimisticAiId,
              text: transcription.trim(),
              userMessageId,
              pronunciationData: pronunciationResult,
              hasAnalysis: get().hasAnalysis,
              span,
              set,
              getConversationStore: () => storeRef!,
            });
          } catch (error: unknown) {
            const errorInfo = thrownErrorToErrorInfo(error);
            span.setStatus({ code: 2, message: errorInfo.message });

            set((s) => ({
              messages: s.messages.map((m) => {
                if (m.role === "assistant" && m.status === "streaming") {
                  return { ...m, status: "error", errorInfo };
                }
                if (m.status === "sending") {
                  return { ...m, status: "complete", analysisPending: false };
                }
                return m;
              }),
              isSendingMessage: false,
              isStreaming: false,
              error: errorInfo.message,
              errorInfo,
            }));
          }
        }
      );
    });
  },

  /**
   * Retry the AI conversation response.
   * Accepts any message ID (user or AI), figures out the pair.
   *
   * - If user message is optimistic (never saved): full resend via sendTextMessage
   * - If user message has server ID: call regenerate endpoint (conversation only, no analysis)
   * - Transport: socket streaming if connected, HTTP otherwise
   */
  retryConversation: async (messageId: string) => {
    const state = get();
    const sessionId = state.currentSessionId;

    // Find the message and its pair
    const message = state.messages.find((m) => m.id === messageId);
    if (!message) {
      log.warn("Cannot retry - message not found", { messageId });
      return;
    }

    // Determine which is the AI message and which is the user message
    let aiMessage: AnalyzedMessage | undefined;
    let userMessage: AnalyzedMessage | undefined;

    if (message.role === "assistant") {
      aiMessage = message;
      // Find paired user message (previous user msg)
      const aiIdx = state.messages.findIndex((m) => m.id === messageId);
      for (let i = aiIdx - 1; i >= 0; i--) {
        if (state.messages[i].role === "user") {
          userMessage = state.messages[i];
          break;
        }
      }
    } else {
      userMessage = message;
      // Find paired AI message (next assistant after user msg)
      const userIdx = state.messages.findIndex((m) => m.id === messageId);
      for (let i = userIdx + 1; i < state.messages.length; i++) {
        if (state.messages[i].role === "assistant") {
          aiMessage = state.messages[i];
          break;
        }
        if (state.messages[i].role === "user") break;
      }
    }

    if (!userMessage) {
      log.warn("Cannot retry - no paired user message found", { messageId });
      return;
    }

    const text =
      userMessage.type === "text" ? userMessage.text : userMessage.transcription;
    const isOptimistic = userMessage.id.startsWith("msg-") || userMessage.id.startsWith("ai-");

    // ── Optimistic user message (never saved) → full resend ──────
    if (isOptimistic || !sessionId) {
      // Remove both messages and resend
      set((s) => ({
        messages: s.messages.filter(
          (m) => m.id !== userMessage!.id && m.id !== aiMessage?.id
        ),
        error: null,
        errorInfo: null,
      }));
      await get().sendTextMessage(text);
      return;
    }

    // ── Server-persisted user message → regenerate AI response via socket ───
    const socket = getSocket();
    if (!socket) {
      log.error("Socket not available for retry");
      set({
        error: i18n.t('errors.api.INTERNAL_ERROR'),
        errorInfo: { type: "network", message: "Socket not available", retryable: true },
      });
      return;
    }

    const initialAiId = aiMessage?.id || "";

    // Prepare AI message for streaming
    if (aiMessage) {
      set((s) => ({
        messages: s.messages.map((m) =>
          m.id === aiMessage!.id
            ? { ...m, text: "", status: "streaming" as const, errorInfo: undefined }
            : m
        ),
        isStreaming: true,
        streamingText: '',
        streamingMessageId: aiMessage!.id,
      }));
    }

    if (getAudioMode() === 'auto_play') {
      useStreamingTTSStore.getState().startStreaming(initialAiId, get().currentSessionId ?? undefined);
    }

    try {
      await streamMessage({
        mode: 'regenerate',
        socket,
        sessionId,
        aiId: initialAiId,
        regenerateMessageId: userMessage!.id,
        set,
        getConversationStore: () => storeRef!,
      });
    } catch {
      // Socket streaming failed — error already set by onError/ACK handler
    }
  },

  /**
   * Clear all messages (for testing or reset)
   */
  clearMessages: () => {
    set({
      messages: [],
      streamingText: '',
      streamingMessageId: null,
      error: null,
      errorInfo: null,
    });
  },
});
